import { reactive, watch, computed } from 'vue';
import qs from 'qs';
import { nanoid } from 'nanoid';

import { config } from '@/kernel/config';
import { PayloadError, ServerError, AppError } from '@/kernel/error';

import { DefaultFilterAdapter } from '@/kernel/adapters';

export const usePayloadQuery = (path, {filters = null, sort = null, multiSort = null, limit = 20, contexts = null, selects = null} = {}) => {
  let fixedFilters = filters;

  const state = reactive({
    data: null,
    loading: false,
    error: null,
    filters: null,
    sort,
    multiSort,
    limit,
    search: null,
    selected: []
  });

  let computedContexts = null;

  if (contexts) {
    computedContexts = computed(() => {
      const fragments = {}

      contexts.forEach((context) => {
        fragments[context.key] = context.adapter(context.key);
      });

      return fragments;
    });

    watch(computedContexts, () => {
      doQuery();
    });
  }

  watch(() => state.filters, () => {
    doQuery();
  });

  watch(() => state.sort, () => {
    doQuery();
  });

  watch(() => state.multiSort, () => {
    doQuery();
  });

  watch(() => state.limit, () => {
    doQuery();
  });

  watch(() => state.search, () => {
    doQuery();
  });

  let fetchController = null;
  let requestId = null;

  const doQuery = async (page = 1) => {
    if (fetchController) {
      fetchController.abort();
      fetchController = null;
    }

    state.loading = true;
    state.error = null;

    fetchController = new AbortController();
    requestId = nanoid();

    const thisRequestId = requestId;

    await new Promise(r => setTimeout(r, 100));

    // await new Promise(r => setTimeout(r, 2000));

    if (requestId !== thisRequestId) {
      return;
    }

    let merged = {...fixedFilters || {}, ...state.filters || {}, ...computedContexts?.value || {}};

    const filters = (Object.keys(merged) || state.search) ? {} : null;

    if (Object.keys(merged)) {
      filters.and = Object.keys(merged).map((key) => {
        if (merged[key].and || merged[key].or) {
          return merged[key];
        } else {
          return {[key]: merged[key]};
        }
      });
    }

    if (state.search) {
      filters.or = state.search.keys.map(key => ({[key]: {like: state.search.value}}));
    }

    try {
      state.data = await doPayloadQuery('GET', path, {
        params: {
          filters,
          sort: state.sort,
          multiSort: state.multiSort,
          limit: state.limit,
          selects,
          page
        },
        controller: fetchController
      });
    } catch(e) {
      if (e.name === 'AbortError') {
        return;
      } else {
        state.error = e;
      }
    }

    state.loading = false;
  }

  const refresh = () => {
    doQuery(getPage());
  }

  const clearFilters = () => {
    state.filters = null;
  }

  const setFilter = (key, value) => {
    if (!state.filters) {
      state.filters = {};
    }

    state.filters = {
      ...state.filters,
      [key]: value
    }
  }

  const unsetFilter = (key) => {
    if (!state.filters) {
      return;
    }

    if (Object.hasOwn(state.filters, key)) {
      delete state.filters[key];

      state.filters = {
        ...state.filters
      }
    }

    if (!Object.keys(state.filters).length) {
      state.filters = null;
    }
  }

  const hasFilters = () => {
    return state.filters !== null;
  }

  const connectFilter = (key, converter = null) => {
    const state = reactive({
      model: null
    });

    if (!converter) {
      converter = DefaultFilterAdapter;
    }

    watch(() => state.model, () => {
      if (state.model === null) {
        unsetFilter(key);
      } else {
        setFilter(key, converter(key, state.model));
      }
    });

    return state;
  }

  const connectSearch = (keys) => {
    const state = reactive({
      model: null
    });

    watch(() => state.model, () => {
      if (!state.model) {
        clearSearch();
      } else {
        setSearch(keys, state.model);
      }
    });

    return state;
  }

  const setSearch = (keys, value) => {
    state.search = {keys, value};
  }

  const clearSearch = () => {
    state.search = null;
  }

  const hasSearch = () => {
    return state.search !== null;
  }

  const setSort = (key, order) => {
    state.sort = {key, order};
  }

  const toggleSort = (key) => {
    if (state.sort?.key === key) {
      setSort(key, state.sort.order === 1 ? -1 : 1);
    } else {
      setSort(key, state.sort?.order || 1);
    }
  }

  const isSortedBy = (key) => {
    return state.sort?.key === key;
  }

  const getSortOrder = () => {
    return state.sort?.order || null;
  }

  const setMultiSort = (multiSort) => {
    state.multiSort = multiSort;
  }

  const setLimit = (limit) => {
    state.limit = limit;
  }

  const getLimit = () => {
    return state.limit;
  }

  const getPageStartIndex = () => {
    if (!state.data?.docs?.length) {
      return 0;
    }

    return ((state.data?.page - 1) * state.limit) + 1;
  }

  const getPageEndIndex = () => {
    if (!state.data?.docs?.length) {
      return 0;
    }

    return getPageStartIndex() + state.data.docs.length - 1;
  }

  const getTotalResults = () => {
    return state.data?.totalDocs || 0;
  }

  const getTotalPages = () => {
    return state.data?.totalPages || 0;
  }

  const setPage = (page) => {
    if (page < 1) {
      page = 1;
    }

    if (page > getTotalPages()) {
      page = getTotalPages();
    }

    doQuery(page);
  }

  const movePage = (amount) => {
    setPage(state.data.page + amount);
  }

  const getPage = () => {
    return state.data?.page || 0;
  }

  const hasError = () => {
    return state.error !== null;
  }

  const getError = () => {
    return state.error;
  }

  const isLoading = () => {
    return state.loading;
  }

  const getItems = () => {
    return state.data?.docs || [];
  }

  const hasItems = () => {
    return state.data?.docs.length > 0;
  }

  const isEmpty = () => {
    return !hasFilters() && !hasSearch() && !hasError() && !hasItems();
  }

  const getSelected = () => {
    if (!state.data?.docs) {
      return [];
    }

    return state.data.docs.filter(item => item.$isSelected);
  }

  const resetSelected = () => {
    if (!state.data?.docs) {
      return;
    }

    state.data.docs.forEach(item => item.$isSelected = false);
  }

  const hasAnySelected = () => {
    return getSelected().length > 0;
  }

  const hasSomeSelected = () => {
    return getSelected().length > 0 && getSelected().length !== state.data?.docs?.length;
  }

  const hasAllSelected = () => {
    return getSelected().length === state.data?.docs?.length;
  }

  const toggleAllSelected = () => {
    if (!state.data?.docs) {
      return;
    }

    if (hasAllSelected()) {
      resetSelected();
    } else if (state.data?.docs) {
      state.data.docs.forEach(item => item.$isSelected = true);
    }
  }

  return {
    state,
    doQuery,
    refresh,
    clearFilters,
    setFilter,
    unsetFilter,
    hasFilters,
    connectFilter,
    connectSearch,
    setSearch,
    clearSearch,
    hasSearch,
    setSort,
    toggleSort,
    isSortedBy,
    getSortOrder,
    setMultiSort,
    setLimit,
    getLimit,
    getPageStartIndex,
    getPageEndIndex,
    getTotalResults,
    getTotalPages,
    setPage,
    movePage,
    getPage,
    hasError,
    getError,
    isLoading,
    getItems,
    hasItems,
    isEmpty,
    getSelected,
    resetSelected,
    hasAnySelected,
    hasSomeSelected,
    hasAllSelected,
    toggleAllSelected
  };
}

export const usePayloadAction = (method, path) => {
  const state = reactive({
    loading: false,
    error: null,
    data: null
  });

  const getState = () => {
    return state;
  }

  const doAction = async ({params = null, body = null} = {}) => {
    state.loading = true;
    state.error = null;

    try {
      state.data = await doPayloadQuery(method, path, {params, body});
      return state.data;
    } catch(e) {
      console.dir(e);
      state.error = e;
      throw e;
    } finally {
      state.loading = false;
    }
  }

  const isLoading = () => {
    return state.loading;
  }

  const hasError = () => {
    return state.error !== null;
  }

  const getError = () => {
    return state.error;
  }

  const hasData = () => {
    return state.data !== null;
  }

  const getData = () => {
    return state.data;
  }

  return {
    state,
    getState,
    doAction,
    isLoading,
    hasError,
    getError,
    hasData,
    getData
  }
}

export const usePayloadRequest = (method, path) => {
  const state = reactive({
    loading: false,
    error: null,
    data: null
  });

  const getState = () => {
    return state;
  }

  const doRequest = async ({params = null, body = null} = {}) => {
    state.loading = true;
    state.error = null;

    try {
      state.data = await doPayloadRequest(method, path, {params, body});
      return state.data;
    } catch(e) {
      console.dir(e);
      state.error = e;
      throw e;
    } finally {
      state.loading = false;
    }
  }

  const isLoading = () => {
    return state.loading;
  }

  const hasError = () => {
    return state.error !== null;
  }

  const getError = () => {
    return state.error;
  }

  const hasData = () => {
    return state.data !== null;
  }

  const getData = () => {
    return state.data;
  }

  const getDocs = () => {
    return state.data?.docs || [];
  }

  return {
    state,
    getState,
    doRequest,
    isLoading,
    hasError,
    getError,
    hasData,
    getData,
    getDocs
  }
}

export const doPayloadQuery = async (method, path, {params = null, body = null, controller = null} = {}) => {
  const {
    filters = null,
    sort = null,
    multiSort = null,
    limit = null,
    page = null,
    depth = null,
    selects = null
  } = params || {};

  let query = {};

  if (filters) {
    query.where = filters;
  }

  if (sort) {
    query.sort = `${sort.order === 1 ? '+' : '-'}${sort.key}`;
  }

  if (multiSort) {
    query.sort = multiSort.map(sort => `${sort.order === 1 ? '+' : '-'}${sort.key}`);
  }

  if (limit) {
    query.limit = limit;
  }

  if (page) {
    query.page = page;
  }

  if (depth) {
    query.depth = depth;
  }

  if (selects) {
    query.select = selects;
  }

  return await doPayloadRequest(method, path, {params: query, body, controller});
}

export const doPayloadRequest = async (method, path, {params = null, body = null, controller = null} = {}) => {
  const queryString = qs.stringify(params);

  const url = new URL(`${config.payloadApiEndpoint}${path}?${queryString}`);

  const response = await fetch(url, {
    method,
    headers: {
      'accepts': 'application/json',
      'content-type': 'application/json'
    },
    credentials: 'include',
    signal: controller?.signal || null,
    body: body ? JSON.stringify(body) : null
  });

  const applicationVersion = response.headers.get('X-Application-Version');

  if (applicationVersion && config.applicationVersion !== applicationVersion) {
    window.dispatchEvent(new CustomEvent('warn_application_version_outdated'));
  }

  let data;

  try {
    data = await response.json();
  } catch(e) {
    throw new AppError('json_error');
  }

  if (!response.ok) {
    if (data.kind === 'ServerError') {
      throw new ServerError(data);
    } else {
      throw new PayloadError(response.status, response.statusText, data);
    }
  }

  return data;
}
